if (!customElements.get('nosht-hero-viewport')) {
  class NoshtHeroViewport extends HTMLElement {
    connectedCallback() {
      this.releaseResources();
      const version = this.lifeVersion = (this.lifeVersion || 0) + 1;
      this.hero = this.parentElement;
      if (!this.hero?.matches('[data-nosht-fit-desktop-viewport]')) return;

      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.desktopQuery = window.matchMedia('(min-width: 1024px)');
      this.desktopQuery.addEventListener('change', () => this.updateMode(), options);
      window.addEventListener('resize', () => this.queueMeasure(), options);
      window.addEventListener('pageshow', () => this.queueMeasure(true), options);
      window.addEventListener('load', () => this.queueMeasure(), options);
      document.addEventListener('DOMContentLoaded', () => this.queueMeasure(true), options);
      ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach((name) => {
        document.addEventListener(name, () => this.queueMeasure(true), options);
      });
      document.fonts?.addEventListener('loadingdone', () => this.queueMeasure(), options);
      document.fonts?.ready.then(() => {
        if (version === this.lifeVersion && this.isConnected) this.queueMeasure();
      });

      this.resizeObserver = new ResizeObserver((entries) => {
        let changed = false;
        entries.forEach((entry) => {
          const size = entry.borderBoxSize?.[0];
          const width = size?.inlineSize ?? entry.contentRect.width;
          const height = size?.blockSize ?? entry.contentRect.height;
          const previous = this.observedSizes.get(entry.target);
          // The height we set through CSS must not become a resize feedback loop.
          if (!previous || previous.width !== width || (entry.target !== this.hero && previous.height !== height)) {
            changed = true;
          }
          this.observedSizes.set(entry.target, { width, height });
        });
        if (changed) this.queueMeasure();
      });
      this.layoutObserver = new MutationObserver((records) => {
        this.queueMeasure(records.some((record) => record.type === 'childList'));
      });
      this.updateMode();
    }

    disconnectedCallback() {
      this.lifeVersion = (this.lifeVersion || 0) + 1;
      this.releaseResources();
    }

    releaseResources() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      this.layoutObserver?.disconnect();
      if (this.measureFrame != null) cancelAnimationFrame(this.measureFrame);
      this.measureFrame = null;
      this.needsRebind = false;
      this.observedSizes = new Map();
      this.hero?.style.removeProperty('--nosht-hero-top');
    }

    updateMode() {
      if (this.desktopQuery.matches) {
        this.queueMeasure(true);
        return;
      }
      this.resizeObserver.disconnect();
      this.layoutObserver.disconnect();
      this.observedSizes.clear();
      if (this.measureFrame != null) cancelAnimationFrame(this.measureFrame);
      this.measureFrame = null;
      this.needsRebind = false;
      this.hero.style.removeProperty('--nosht-hero-top');
    }

    queueMeasure(rebind = false) {
      if (!this.isConnected || !this.desktopQuery?.matches) return;
      this.needsRebind = this.needsRebind || rebind;
      if (this.measureFrame != null) return;
      this.measureFrame = requestAnimationFrame(() => {
        this.measureFrame = null;
        if (!this.isConnected || !this.desktopQuery.matches) return;
        if (this.needsRebind) {
          this.needsRebind = false;
          this.observeLayout();
        }
        this.measure();
      });
    }

    observeLayout() {
      this.resizeObserver.disconnect();
      this.layoutObserver.disconnect();
      this.observedSizes.clear();
      const sizes = new Set();
      const ancestors = new Set();
      const precedingSections = new Set();

      // Include preceding siblings at every level: announcements/header are body
      // siblings, while another content section can be a sibling inside main.
      for (let current = this.hero; current; current = current.parentElement) {
        sizes.add(current);
        if (current !== this.hero) ancestors.add(current);
        for (let sibling = current.previousElementSibling; sibling; sibling = sibling.previousElementSibling) {
          if (/^(SCRIPT|STYLE|LINK|TEMPLATE|META|NOSCRIPT)$/.test(sibling.tagName)) continue;
          sizes.add(sibling);
          if (sibling.matches('.shopify-section')) precedingSections.add(sibling);
        }
        if (current === document.body) break;
      }

      sizes.forEach((target) => this.resizeObserver.observe(target));
      ancestors.forEach((target) => {
        this.layoutObserver.observe(target, {
          childList: true,
          attributes: true,
          attributeFilter: ['class', 'style'],
        });
      });
      precedingSections.forEach((target) => {
        this.layoutObserver.observe(target, { attributes: true, attributeFilter: ['class', 'style'] });
      });
      // No subtree observation: the marquee's per-frame offset and clones are
      // irrelevant to document flow. The sticky classes on html are excluded too.
    }

    measure() {
      // A sticky/hidden header changes its screen position, not this document top.
      const top = this.hero.getBoundingClientRect().top + window.scrollY;
      if (!Number.isFinite(top)) return;
      const value = `${Math.round(Math.max(0, top) * 100) / 100}px`;
      if (this.hero.style.getPropertyValue('--nosht-hero-top') !== value) {
        this.hero.style.setProperty('--nosht-hero-top', value);
      }
    }
  }

  customElements.define('nosht-hero-viewport', NoshtHeroViewport);
}
